import FormattedMessage from '@/components/formatted-message';
import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/input-password';
import { toast } from '@/hooks/use-toast';
import {
    generateMagicLinkForLoginMutationFn,
    loginMutationFn,
    mfaEmailSetupQueryFn,
    verifyRecoveryEmailForLoginMutationFn,
} from '@/lib/api';
import { useZodForm } from '@/lib/zod';
import { CredentialsLoginFormSchema, OnlyEmailSchema } from '@/schemas';
import { MFAMethod } from '@/types/enums';
import { TCredentialsLogin, TRecoverEmail } from '@/types/types';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, AtSign, Loader, MailCheckIcon } from 'lucide-react';
import React, { Suspense, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const LazyMFAPicker = React.lazy(() => import('@/components/mfa/mfa-picker'));

export default function Login() {
    const [isEmailVerified, setIsEmailVerified] = React.useState(true);
    const [userEmail, setUserEmail] = React.useState('');
    const [newLoginMessage, setNewLoginMessage] = React.useState('');
    const [isDeactivated, setIsDeactivated] = React.useState(false);
    const [mfaPickerOpen, setMfaPickerOpen] = React.useState(false);
    const [isUsingRecoveryEmail, setIsUsingRecoveryEmail] =
        React.useState(false);
    const [isUsingMagicLink, setIsUsingMagicLink] = React.useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const { fromPicker, email } = location.state || {};

    const { mutate, isPending } = useMutation({
        mutationFn: loginMutationFn,
    });

    const { mutate: generateEmailFACode, isPending: isGeneratingCode } =
        useMutation({
            mutationFn: mfaEmailSetupQueryFn,
        });

    const credentialsForm = useZodForm(CredentialsLoginFormSchema, {
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const { mutate: onlyEmailMutation, isPending: isOnlyEmailPending } =
        useMutation({
            mutationFn: isUsingRecoveryEmail
                ? verifyRecoveryEmailForLoginMutationFn
                : generateMagicLinkForLoginMutationFn,
        });

    const onlyEmailform = useZodForm(OnlyEmailSchema, {
        defaultValues: {
            email: '',
        },
    });

    const onSubmitCredentials = (values: TCredentialsLogin) => {
        mutate(values, {
            onSuccess: ({
                data: { user, mfaRequired, mfaMethod, message, isDeactivated },
            }) => {
                if (mfaRequired) {
                    if (
                        mfaMethod.indexOf(MFAMethod.EMAIL) !== -1 &&
                        mfaMethod.indexOf(MFAMethod.TOTP) !== -1
                    ) {
                        setUserEmail(values.email);
                        setNewLoginMessage(message);
                        setIsDeactivated(isDeactivated);
                        setMfaPickerOpen(true);
                        return;
                    } else if (mfaMethod.indexOf(MFAMethod.EMAIL) !== -1) {
                        generateEmailFACode(
                            {
                                email: values.email,
                                method: MFAMethod.EMAIL,
                            },
                            {
                                onSuccess: () => {
                                    navigate(`/verify-mfa`, {
                                        state: {
                                            email: values.email,
                                            method: MFAMethod.EMAIL,
                                            isEmailSent: true,
                                            isDeactivated,
                                            message,
                                        },
                                    });
                                },
                            },
                        );
                        return;
                    } else if (mfaMethod.indexOf(MFAMethod.TOTP) !== -1) {
                        navigate(`/verify-mfa`, {
                            state: {
                                email: values.email,
                                method: MFAMethod.TOTP,
                                isEmailSent: false,
                                message,
                                isDeactivated,
                            },
                        });
                        return;
                    }
                }
                toast({
                    title: `Hello ${user.name || 'Friend'}!`,
                    description: <FormattedMessage message={message} />,
                });
                navigate('/mfa');
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (error: any) => {
                toast({
                    title: 'Error',
                    description: (
                        <FormattedMessage message={error.message || error} />
                    ),
                    variant: 'destructive',
                });
                if (error.errorCode === 'AUTH_EMAIL_IS_NOT_VERIFIED') {
                    setIsEmailVerified(false);
                }
            },
        });
    };

    const onSubmitOnlyEmail = async (values: TRecoverEmail) => {
        onlyEmailMutation(values, {
            onSuccess: ({
                data: { magicLink, user, mfaRequired, mfaMethod, message },
            }) => {
                if (mfaRequired && mfaMethod) {
                    if (
                        mfaMethod.indexOf(MFAMethod.EMAIL) !== -1 &&
                        mfaMethod.indexOf(MFAMethod.TOTP) !== -1
                    ) {
                        setUserEmail(user.email);
                        setNewLoginMessage(message);
                        setMfaPickerOpen(true);
                        return;
                    } else if (mfaMethod.indexOf(MFAMethod.EMAIL) !== -1) {
                        generateEmailFACode(
                            {
                                email: user.email,
                                method: MFAMethod.EMAIL,
                            },
                            {
                                onSuccess: () => {
                                    navigate(`/verify-mfa`, {
                                        state: {
                                            email: user.email,
                                            method: MFAMethod.EMAIL,
                                            isEmailSent: true,
                                            message,
                                        },
                                    });
                                },
                            },
                        );
                        return;
                    } else if (mfaMethod.indexOf(MFAMethod.TOTP) !== -1) {
                        navigate(`/verify-mfa`, {
                            state: {
                                email: user.email,
                                method: MFAMethod.TOTP,
                                isEmailSent: false,
                                message,
                            },
                        });
                        return;
                    }
                }
                toast({
                    title: `Hello ${user ? user?.name : 'Friend'}!`,
                    description: <FormattedMessage message={message} />,
                });
                if (magicLink) {
                    navigate('/');
                    return;
                }
                navigate('/mfa');
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (error: any) => {
                toast({
                    title: 'Error',
                    description: <FormattedMessage message={error.message} />,
                    variant: 'destructive',
                });
                if (error.errorCode === 'AUTH_EMAIL_IS_NOT_VERIFIED') {
                    setIsEmailVerified(false);
                }
            },
        });
    };

    useEffect(() => {
        if (fromPicker && email) {
            setMfaPickerOpen(true);
            setUserEmail(email);
            navigate('.', { replace: true, state: {} });
        }
    }, [email, fromPicker, navigate]);

    return (
        <>
            {isEmailVerified ? (
                <main className='w-full min-h-screen max-w-full pt-10'>
                    <div className='w-full h-full p-5 rounded-md'>
                        <Logo size='100px' />

                        <h1 className='text-xl tracking-[-0.16px] font-bold mb-1.5 mt-8 text-center'>
                            Log in to your account
                        </h1>
                        <p className='mb-8 text-center text-base text-muted-foreground font-normal'>
                            Don&apos;t have an account?{' '}
                            <Link
                                className='text-primary'
                                to='/signup'>
                                Sign up
                            </Link>
                        </p>

                        {(isUsingRecoveryEmail || isUsingMagicLink) && (
                            <>
                                <p className='mb-6 text-center text-[15px] dark:text-[#f1f7feb5] font-normal'>
                                    {isUsingRecoveryEmail
                                        ? 'We will send a code to your primary email address to help you identify it. You must still have access to that inbox.'
                                        : 'We will send you a link to your primary email to help you login by verifying the link.'}
                                </p>
                                <div className='mt-2'>
                                    <Form {...onlyEmailform}>
                                        <form
                                            onSubmit={onlyEmailform.handleSubmit(
                                                onSubmitOnlyEmail,
                                            )}
                                            className='w-full mt-6 flex flex-col gap-4 '>
                                            <FormField
                                                control={onlyEmailform.control}
                                                name='email'
                                                render={({ field }) => (
                                                    <FormItem className='flex flex-col items-center'>
                                                        <FormLabel className='mb-1 font-normal text-muted-foreground text-left w-full px-0'>
                                                            <div className='flex items-center gap-2 '>
                                                                <AtSign
                                                                    size={15}
                                                                />{' '}
                                                                {isUsingRecoveryEmail
                                                                    ? 'Recovery'
                                                                    : 'Primary'}{' '}
                                                                Email
                                                            </div>
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                placeholder='user@example.com'
                                                            />
                                                        </FormControl>
                                                        <FormMessage className='w-full !text-start' />
                                                    </FormItem>
                                                )}
                                            />
                                            <Button
                                                disabled={
                                                    isOnlyEmailPending ||
                                                    isGeneratingCode
                                                }
                                                type='submit'
                                                className='w-full h-[40px] mt-2'>
                                                {(isOnlyEmailPending ||
                                                    isGeneratingCode) && (
                                                    <Loader className='mr-2 animate-spin' />
                                                )}
                                                Continue
                                                <ArrowRight />
                                            </Button>
                                        </form>
                                    </Form>
                                </div>
                            </>
                        )}
                        {!isUsingRecoveryEmail && !isUsingMagicLink && (
                            <Form {...credentialsForm}>
                                <form
                                    onSubmit={credentialsForm.handleSubmit(
                                        onSubmitCredentials,
                                    )}>
                                    <div className='mb-4'>
                                        <FormField
                                            control={credentialsForm.control}
                                            name='email'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className='dark:text-[#f1f7feb5] text-sm'>
                                                        Email
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            placeholder='user@example.com'
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className='mb-4'>
                                        <FormField
                                            control={credentialsForm.control}
                                            name='password'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className='dark:text-[#f1f7feb5] text-sm'>
                                                        Password
                                                    </FormLabel>
                                                    <FormControl>
                                                        <PasswordInput
                                                            placeholder='••••••••••••'
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className='mb-4 flex w-full items-center justify-end'>
                                        <Link
                                            className='text-sm dark:text-white'
                                            to={'/forgot-password'}>
                                            Forgot your password?
                                        </Link>
                                    </div>
                                    <Button
                                        disabled={isPending || isGeneratingCode}
                                        className='w-full text-[15px] h-[40px] text-white font-semibold'
                                        type='submit'>
                                        {(isPending || isGeneratingCode) && (
                                            <Loader className='animate-spin' />
                                        )}
                                        Sign in
                                        <ArrowRight />
                                    </Button>

                                    <div className='my-6 flex items-center justify-center'>
                                        <div
                                            aria-hidden='true'
                                            className='h-px w-full bg-[#eee] dark:bg-[#d6ebfd30]'
                                            data-orientation='horizontal'
                                            role='separator'></div>
                                        <span className='mx-4 text-xs dark:text-[#f1f7feb5] font-normal'>
                                            OR
                                        </span>
                                        <div
                                            aria-hidden='true'
                                            className='h-px w-full bg-[#eee] dark:bg-[#d6ebfd30]'
                                            data-orientation='horizontal'
                                            role='separator'></div>
                                    </div>
                                </form>
                            </Form>
                        )}

                        {!isUsingRecoveryEmail && !isUsingMagicLink && (
                            <>
                                <Button
                                    variant='outline'
                                    type='button'
                                    onClick={() =>
                                        setIsUsingRecoveryEmail((prev) => !prev)
                                    }
                                    className='w-full h-[40px] my-6'>
                                    {isUsingRecoveryEmail
                                        ? 'Use Credentials'
                                        : 'Use Recovery Email'}
                                </Button>
                                <Button
                                    variant='outline'
                                    type='button'
                                    onClick={() => {
                                        setIsUsingMagicLink(true);
                                    }}
                                    className='w-full h-[40px]'>
                                    Email Magic Link
                                </Button>
                            </>
                        )}
                        {(isUsingRecoveryEmail || isUsingMagicLink) && (
                            <Link to={'/login'}>
                                <Button
                                    onClick={() => {
                                        setIsUsingMagicLink(false);
                                        setIsUsingRecoveryEmail(false);
                                    }}
                                    variant={'outline'}
                                    className='w-full h-[40px] my-4'>
                                    Go Back
                                </Button>
                            </Link>
                        )}

                        <p className='text-xs dark:text-slate- font-normal mt-7'>
                            By signing in, you agree to our{' '}
                            <a
                                className='text-primary hover:underline'
                                href='#'>
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a
                                className='text-primary hover:underline'
                                href='#'>
                                Privacy Policy
                            </a>
                            .
                        </p>
                    </div>
                </main>
            ) : (
                <div className='w-full h-[80vh] flex flex-col gap-2 items-center justify-center rounded-md'>
                    <div className='size-[48px]'>
                        <MailCheckIcon
                            size='48px'
                            className='animate-bounce'
                        />
                    </div>
                    <h2 className='text-center text-xl tracking-[-0.16px] dark:text-[#fcfdffef] font-bold'>
                        Your email is not verified
                        <br />
                        Check your email
                    </h2>
                    <p className='mb-2 text-center text-sm text-muted-foreground dark:text-[#f1f7feb5] font-normal'>
                        We just sent a verification link to{' '}
                        {credentialsForm.getValues().email}.
                    </p>
                    <Link to='/'>
                        <Button
                            className='h-[40px]'
                            onClick={() => setIsEmailVerified(true)}>
                            Home
                            <ArrowRight />
                        </Button>
                    </Link>
                </div>
            )}
            <Suspense fallback={'Loading...'}>
                <LazyMFAPicker
                    mfaOpen={(fromPicker || userEmail) && mfaPickerOpen}
                    setMfaOpen={setMfaPickerOpen}
                    userEmail={userEmail}
                    message={newLoginMessage}
                    isDeactivated={isDeactivated}
                />
            </Suspense>
        </>
    );
}
